import { OnDestroy } from '@angular/core';
/*!
 * Copyright (c) Trimble Inc. All rights reserved
 */
/* istanbul ignore file */
import { LoaderService } from 'src/app/sharedServices/loader.service';
import { AzureBlobStorageService } from 'api/services';
import { UserHandlerService } from 'src/app/sharedServices/user-handler.service';
/*!
 * Copyright (c) Trimble Inc. All rights reserved
 */
import { environment } from 'src/environments/environment';
import { Injectable } from '@angular/core';
import * as signalR from '@microsoft/signalr';
import { FittingSizeManagementService } from './fitting-size-management.service';
import { Subscription, interval } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class SignalRService {
  private hubConnection: signalR.HubConnection;
  public data: any[];
  public canDownload: boolean;
  public canCopy: boolean;
  private sub: Subscription;

  constructor(
    private userService: UserHandlerService,
    private azureService: AzureBlobStorageService,
    private loaderService: LoaderService,
    private sharedService: FittingSizeManagementService
  ) {
    this.canDownload = true;
    this.canCopy = true;
  }

  OnDestroy() {
    this.sub.unsubscribe();
  }

  public tryStartConnection(): void {
    if (
      this.userService.getCurrentUserId() &&
      this.hubConnection.state === signalR.HubConnectionState.Disconnected
    ) {
      this.startConnection();
    }
  }

  public buildConnection = () => {
    this.hubConnection = new signalR.HubConnectionBuilder()
      .withUrl(
        `${
          environment.rootURL
        }/hub?userId=${this.userService.getCurrentUserId()}`,
        {
          skipNegotiation: true,
          transport: signalR.HttpTransportType.WebSockets,
        }
      )
      .configureLogging(signalR.LogLevel.Information)
      .withAutomaticReconnect()
      .build();
    this.hubConnection.serverTimeoutInMilliseconds = 3600000;
  };

  public startConnection = () => {
    console.log('Connection Started...');
    this.progressSpinner();
    this.copyItemsCompleted();
    this.addDownloadConnection();
    this.hubConnection
      .start()
      .then(() => {
        this.sub = interval(10000).subscribe((res) => {
          this.hubConnection.invoke(
            'IsUserConnected',
            this.userService.getCurrentUserId()
          );
          console.log('Ping sent from user');
        });
      })
      .catch((err) => {
        console.log('Error while starting connection: ' + err);
        setTimeout(() => {
          this.startConnection();
        }, 3000);
      });
  };

  public progressSpinner = () => {
    this.hubConnection.on('ProgressSpinner', (data) => {
      if (!this.loaderService.getProgressBarDisplayStatus()) {
        this.loaderService.displayProgressBar(true);
      }
      this.loaderService.setProgresForProgressBar(data);
    });
  };

  public copyItemsCompleted() {
    this.hubConnection.on('CopyItemsCompleted', (copiedSuccesfully: any) => {
      this.loaderService.displayProgressBar(false);
      if (copiedSuccesfully) {
        this.sharedService.openSnackBarWithAction(
          'Processed Items: ' +
            copiedSuccesfully.processedItems +
            ' | Copied Items: ' +
            copiedSuccesfully.copiedBlobItems,
          'OK'
        );
      }
      this.canCopy = true;
    });
  }

  public addDownloadConnection = () => {
    this.hubConnection.on(
      'DownloadCompleted',
      (completedSuccesfully: boolean) => {
        this.loaderService.displayProgressBar(false);
        if (completedSuccesfully) {
          this.sharedService.openSnackBar('Download Started');
          this.azureService
            .getDownloadUrl({ userId: this.userService.getCurrentUserId() })
            .subscribe((result) => {
              window.open(result);
            });
        }
        this.canDownload = true;
      }
    );
  };
}
